import type {
  AnyProtocolMessage,
  IceServerPayload,
  PeerLeaveReason,
  PeerRole,
  RoomCredential,
} from '@screenshare/protocol';
import { ROOM_DURATION_LIMITS } from '@screenshare/protocol';
import { DataChannelManager } from '../chat';
import { CaptureError, MediaCaptureManager, type CaptureSession } from '../media';
import { AdaptiveQualityController, SenderQualityApplier } from '../quality';
import { RecoveryController } from '../recovery';
import { StatsCollector, type ConnectionMetrics } from '../stats';
import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import { PeerConnectionManager, SignalingClient, withProvisionedIceServers } from '../webrtc';
import { RoomApiClient, RoomApiError } from './RoomApiClient';
import type { JoinRoomInput, SessionManagerOptions, SessionSnapshot } from './types';

interface SessionEvents {
  readonly change: { readonly snapshot: SessionSnapshot };
}

const INITIAL_SNAPSHOT: SessionSnapshot = Object.freeze({
  phase: 'idle',
  role: null,
  roomCode: null,
  pin: null,
  inviteUrl: null,
  expiresAt: null,
  localStream: null,
  remoteStream: null,
  viewerCount: 0,
  locked: false,
  microphoneEnabled: false,
  microphonePending: false,
  sharedAudioState: 'not-requested',
  recovery: null,
  metrics: null,
  iceState: null,
  chat: null,
  error: null,
  endedMessage: null,
});

export class SessionManager extends TypedEventEmitter<SessionEvents> {
  private readonly roomApi: RoomApiClient;
  private readonly captureManager: MediaCaptureManager;
  private readonly microphoneProvider;
  private readonly iceConfiguration: RTCConfiguration;
  private signaling: SignalingClient | null = null;
  private peer: PeerConnectionManager | null = null;
  private chat: DataChannelManager | null = null;
  private stats: StatsCollector | null = null;
  private recovery: RecoveryController | null = null;
  private quality: AdaptiveQualityController | null = null;
  private microphoneStream: MediaStream | null = null;
  private microphoneEndedCleanup: (() => void) | null = null;
  private readonly unsubscribers: Array<() => void> = [];
  private cleanupPromise: Promise<void> | null = null;
  private roomExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private turnRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private managedTurnEnabled = false;
  private operationEpoch = 0;
  private disposed = false;
  private _snapshot = INITIAL_SNAPSHOT;

  constructor(options: SessionManagerOptions) {
    super();
    this.roomApi = options.roomApi ?? new RoomApiClient({ baseUrl: options.apiBaseUrl });
    this.captureManager = options.captureManager ?? new MediaCaptureManager();
    this.microphoneProvider = options.microphoneProvider ?? browserMicrophoneProvider();
    this.iceConfiguration = options.iceConfiguration ?? { iceServers: [] };
  }

  get snapshot(): SessionSnapshot {
    return this._snapshot;
  }

  get captureSupported(): boolean {
    return this.captureManager.capabilities.supported;
  }

  get fullscreenSupported(): boolean {
    return (
      typeof document !== 'undefined' &&
      document.fullscreenEnabled === true &&
      typeof Element !== 'undefined' &&
      'requestFullscreen' in Element.prototype
    );
  }

  subscribe(listener: (snapshot: SessionSnapshot) => void): () => void {
    listener(this._snapshot);
    return this.on('change', ({ snapshot }) => listener(snapshot));
  }

  async startHosting(durationSeconds: number | null = null): Promise<void> {
    this.requireIdle();
    if (
      durationSeconds !== null &&
      (!Number.isSafeInteger(durationSeconds) ||
        durationSeconds < ROOM_DURATION_LIMITS.minimumSeconds ||
        durationSeconds > ROOM_DURATION_LIMITS.maximumSeconds)
    ) {
      this.update({ error: 'Choose a session duration from 10 seconds up to 24 hours.' });
      return;
    }
    const operationEpoch = ++this.operationEpoch;
    this.update({ phase: 'starting', role: 'host', error: null, endedMessage: null });
    try {
      const capture = await this.captureManager.start({ sharedAudio: true });
      this.requireCurrentOperation(operationEpoch);
      const room = await this.roomApi.createRoom(durationSeconds);
      this.requireCurrentOperation(operationEpoch);
      await this.startTransport(
        'host',
        room.roomCode,
        { type: 'host-token', value: room.hostToken },
        capture,
        operationEpoch,
      );
      this.unsubscribers.push(
        capture.onEnded(() => {
          if (this._snapshot.phase === 'active') {
            void this.end('Screen sharing stopped.');
          }
        }),
      );
      const screenAudioTrack = capture.audioTracks[0];
      if (screenAudioTrack) {
        const handleScreenAudioEnded = () => {
          if (this._snapshot.phase !== 'active') return;
          this.update({ sharedAudioState: 'unavailable' });
          void this.peer?.setLocalTrack('screen-audio').catch(() => undefined);
        };
        screenAudioTrack.addEventListener('ended', handleScreenAudioEnded, { once: true });
        this.unsubscribers.push(() =>
          screenAudioTrack.removeEventListener('ended', handleScreenAudioEnded),
        );
      }
      this.update({
        phase: 'active',
        roomCode: room.roomCode,
        pin: room.pin,
        inviteUrl: room.inviteUrl,
        expiresAt: room.expiresAt,
        localStream: capture.stream,
        sharedAudioState: capture.sharedAudioState,
      });
      this.scheduleRoomExpiry(room.expiresAt);
    } catch (cause) {
      await this.cleanup(false);
      if (cause instanceof SessionOperationCancelled || !this.isCurrentOperation(operationEpoch)) {
        return;
      }
      this._snapshot = Object.freeze({
        ...INITIAL_SNAPSHOT,
        error: friendlyStartError(cause),
      });
      this.publish();
    }
  }

  async joinRoom(input: JoinRoomInput): Promise<void> {
    this.requireIdle();
    const roomCode = input.roomCode.replace(/\D/gu, '');
    if (!/^\d{6}$/u.test(roomCode)) {
      this.update({ error: 'Enter the six-digit room code.' });
      return;
    }
    const credential = viewerCredential(input);
    if (credential === null) {
      this.update({ error: 'Enter the four-digit PIN.' });
      return;
    }

    const operationEpoch = ++this.operationEpoch;
    this.update({
      phase: 'starting',
      role: 'viewer',
      roomCode,
      error: null,
      endedMessage: null,
    });
    try {
      await this.startTransport('viewer', roomCode, credential, null, operationEpoch);
      this.requireCurrentOperation(operationEpoch);
      this.update({ phase: 'active' });
      this.scheduleRoomExpiry(this._snapshot.expiresAt);
    } catch (cause) {
      await this.cleanup(false);
      if (cause instanceof SessionOperationCancelled || !this.isCurrentOperation(operationEpoch)) {
        return;
      }
      this._snapshot = Object.freeze({
        ...INITIAL_SNAPSHOT,
        role: 'viewer',
        roomCode,
        error: friendlyStartError(cause),
      });
      this.publish();
    }
  }

  async toggleMicrophone(): Promise<void> {
    if (this._snapshot.phase !== 'active' || this._snapshot.role !== 'host') return;
    const peer = this.peer;
    const operationEpoch = this.operationEpoch;
    if (peer === null || this._snapshot.microphonePending) return;

    if (this._snapshot.microphoneEnabled) {
      this.update({ microphonePending: true });
      try {
        await peer.setLocalTrack('microphone');
        if (!this.isCurrentOperation(operationEpoch) || this._snapshot.phase !== 'active') return;
        this.detachMicrophoneEndedListener();
        stopStream(this.microphoneStream);
        this.microphoneStream = null;
        this.update({ microphoneEnabled: false, microphonePending: false });
      } catch {
        if (!this.isCurrentOperation(operationEpoch)) return;
        this.update({ microphonePending: false, error: 'The microphone could not be turned off.' });
      }
      return;
    }

    if (this.microphoneProvider === null) {
      this.update({ error: 'Microphone access is not supported in this browser.' });
      return;
    }
    this.update({ microphonePending: true, error: null });
    let stream: MediaStream | null = null;
    try {
      stream = await this.microphoneProvider.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const track = stream.getAudioTracks()[0];
      if (!track) throw new Error('No microphone track was returned.');
      await peer.setLocalTrack('microphone', track, stream);
      if (!this.isCurrentOperation(operationEpoch) || this._snapshot.phase !== 'active') {
        stopStream(stream);
        return;
      }
      this.microphoneStream = stream;
      const handleEnded = () => {
        if (this.microphoneStream !== stream) return;
        this.detachMicrophoneEndedListener();
        this.microphoneStream = null;
        this.update({ microphoneEnabled: false, microphonePending: false });
        void this.peer?.setLocalTrack('microphone').catch(() => undefined);
      };
      track.addEventListener('ended', handleEnded, { once: true });
      this.microphoneEndedCleanup = () => track.removeEventListener('ended', handleEnded);
      this.update({ microphoneEnabled: true, microphonePending: false });
    } catch {
      stopStream(stream);
      if (!this.isCurrentOperation(operationEpoch)) return;
      this.update({
        microphoneEnabled: false,
        microphonePending: false,
        error: 'Microphone access was not granted. Screen sharing is still active.',
      });
    }
  }

  setLocked(locked: boolean): void {
    if (this._snapshot.role !== 'host' || this._snapshot.phase !== 'active') return;
    try {
      this.signaling?.setRoomLocked(locked);
      this.update({ locked, error: null });
    } catch {
      this.update({ error: 'The session lock could not be changed. Please try again.' });
    }
  }

  removeViewer(): void {
    if (
      this._snapshot.role !== 'host' ||
      this._snapshot.phase !== 'active' ||
      this._snapshot.viewerCount === 0
    ) {
      return;
    }
    try {
      this.signaling?.removeViewer();
      this.update({ viewerCount: 0, error: null });
    } catch {
      this.update({ error: 'The viewer could not be disconnected. Please try again.' });
    }
  }

  retryConnection(): void {
    this.recovery?.retryNow();
  }

  clearError(): void {
    if (this._snapshot.error !== null) this.update({ error: null });
  }

  async end(message = 'The session has ended.'): Promise<void> {
    if (this._snapshot.phase === 'idle' || this._snapshot.phase === 'ended') return;
    this.operationEpoch += 1;
    this.update({ phase: 'ending' });
    await this.cleanup(true);
    this._snapshot = Object.freeze({
      ...INITIAL_SNAPSHOT,
      phase: 'ended',
      role: this._snapshot.role,
      endedMessage: message,
    });
    this.publish();
  }

  reset(): void {
    if (this._snapshot.phase !== 'idle' && this._snapshot.phase !== 'ended') return;
    this._snapshot = INITIAL_SNAPSHOT;
    this.publish();
  }

  dispose(): void {
    this.disposed = true;
    this.operationEpoch += 1;
    void this.cleanup(false);
    this.captureManager.dispose();
    this.clearListeners();
  }

  private async startTransport(
    role: PeerRole,
    roomCode: string,
    credential: RoomCredential,
    capture: CaptureSession | null,
    operationEpoch: number,
  ): Promise<void> {
    const signaling = new SignalingClient({
      url: this.roomApi.signalingUrl(roomCode),
      roomCode,
      role,
      credential,
    });
    this.signaling = signaling;
    const joined = await signaling.connect();
    this.requireCurrentOperation(operationEpoch);

    const peer = new PeerConnectionManager({
      role,
      signaling,
      configuration: withProvisionedIceServers(this.iceConfiguration, joined.iceServers),
    });
    this.peer = peer;
    const remoteStream = new MediaStream();
    this.installSessionListeners(signaling, peer, remoteStream, role);
    peer.start(
      capture
        ? {
            video: capture.videoTrack,
            screenStream: capture.stream,
            ...(capture.audioTracks[0] ? { screenAudio: capture.audioTracks[0] } : {}),
          }
        : {},
    );
    const connection = peer.connection;
    if (connection === null) throw new Error('The browser could not create a peer connection.');

    const chat = new DataChannelManager({ role });
    chat.bindPeerConnection(connection);
    this.chat = chat;
    const stats = new StatsCollector(connection);
    this.stats = stats;
    const recovery = new RecoveryController({ signaling, peer, role, metricsSource: stats });
    this.recovery = recovery;
    this.unsubscribers.push(
      recovery.on('statechange', ({ current }) => this.update({ recovery: current })),
      stats.subscribe((metrics) => this.handleMetrics(metrics, role)),
    );

    if (role === 'host') {
      const sender = peer.getSender('video');
      if (sender !== null) {
        const quality = new AdaptiveQualityController();
        const applier = new SenderQualityApplier(sender);
        this.quality = quality;
        this.unsubscribers.push(
          quality.on('decision', ({ decision }) => {
            void applier.apply(decision).catch(() => {
              this.update({
                error: 'Automatic quality adjustment is unavailable in this browser.',
              });
            });
          }),
        );
        void applier.apply(quality.target).catch(() => {
          // Browsers may expose sender controls only after negotiation; later decisions retry them.
        });
      }
    }

    this.update({
      role,
      roomCode,
      expiresAt: joined.expiresAt,
      locked: joined.locked,
      remoteStream,
      chat,
      recovery: recovery.status,
    });
    recovery.start();
    stats.start();
    this.managedTurnEnabled = joined.iceServers.length > 0;
    this.scheduleTurnRefresh(joined.iceServersExpiresAt);
  }

  private installSessionListeners(
    signaling: SignalingClient,
    peer: PeerConnectionManager,
    remoteStream: MediaStream,
    role: PeerRole,
  ): void {
    this.unsubscribers.push(
      signaling.on('message', ({ message }) => this.handleProtocolMessage(message)),
      peer.on('peerready', () => {
        if (role === 'host') this.update({ viewerCount: 1 });
      }),
      peer.on('peerleft', ({ reason }) => {
        if (role === 'host' && (reason === 'left' || reason === 'kicked')) {
          this.update({ viewerCount: 0 });
          if (peer.connection) this.chat?.bindPeerConnection(peer.connection);
        } else {
          void this.end(peerLeftMessage(reason));
        }
      }),
      peer.on('peerreset', () => {
        if (role === 'host' && peer.connection) this.chat?.bindPeerConnection(peer.connection);
      }),
      peer.on('remotetrack', ({ track }) => {
        if (!remoteStream.getTracks().some((existing) => existing.id === track.id)) {
          remoteStream.addTrack(track);
          const handleEnded = () => {
            remoteStream.removeTrack(track);
            if (this._snapshot.phase === 'active') this.update({ remoteStream });
          };
          track.addEventListener('ended', handleEnded, { once: true });
          this.unsubscribers.push(() => track.removeEventListener('ended', handleEnded));
          this.update({ remoteStream });
        }
      }),
      peer.on('iceconnectionstatechange', ({ state }) => this.update({ iceState: state })),
    );
  }

  private handleProtocolMessage(message: AnyProtocolMessage): void {
    if (message.type === 'room.lock') this.update({ locked: true });
    else if (message.type === 'room.unlock') this.update({ locked: false });
    else if (message.type === 'room.error') {
      if (message.payload.code === 'VIEWER_REMOVED') {
        void this.end('The host disconnected you from this session.');
      } else if (message.payload.code === 'ROOM_EXPIRED') {
        void this.end('This session has expired.');
      }
    } else if (message.type === 'room.joined') {
      this.applyTurnCredentials(message.payload.iceServers, message.payload.iceServersExpiresAt);
    } else if (message.type === 'turn.credentials') {
      this.applyTurnCredentials(message.payload.iceServers, message.payload.expiresAt);
    }
  }

  private applyTurnCredentials(
    iceServers: readonly IceServerPayload[],
    expiresAt: string | null,
  ): void {
    if (iceServers.length > 0) {
      this.managedTurnEnabled = true;
      const configuration = withProvisionedIceServers(this.iceConfiguration, iceServers);
      this.peer?.updateIceServers(configuration.iceServers ?? []);
      this.scheduleTurnRefresh(expiresAt);
    } else if (this.managedTurnEnabled && this._snapshot.phase === 'active') {
      this.scheduleTurnRefresh(new Date(Date.now() + 120_000).toISOString());
    }
  }

  private handleMetrics(metrics: ConnectionMetrics, role: PeerRole): void {
    this.update({ metrics });
    if (role === 'host') this.quality?.observe(metrics);
  }

  private cleanup(notifyPeer: boolean): Promise<void> {
    if (this.cleanupPromise !== null) return this.cleanupPromise;
    const operation = this.performCleanup(notifyPeer).finally(() => {
      if (this.cleanupPromise === operation) this.cleanupPromise = null;
    });
    this.cleanupPromise = operation;
    return operation;
  }

  private async performCleanup(notifyPeer: boolean): Promise<void> {
    if (this.roomExpiryTimer !== null) clearTimeout(this.roomExpiryTimer);
    this.roomExpiryTimer = null;
    if (this.turnRefreshTimer !== null) clearTimeout(this.turnRefreshTimer);
    this.turnRefreshTimer = null;
    this.managedTurnEnabled = false;
    for (const unsubscribe of this.unsubscribers.splice(0)) unsubscribe();
    this.stats?.destroy();
    this.stats = null;
    this.quality = null;
    this.chat?.endSession();
    this.chat = null;

    const recovery = this.recovery;
    this.recovery = null;
    if (recovery !== null) {
      if (notifyPeer) await recovery.terminate();
      else {
        recovery.stop();
        await this.peer?.close({ notifyPeer: false });
        this.signaling?.close({ forgetResumeToken: true });
      }
    } else {
      await this.peer?.close({ notifyPeer, reason: leaveReason(this._snapshot.role) });
      this.signaling?.close({ forgetResumeToken: true });
    }
    this.peer = null;
    this.signaling = null;

    this.detachMicrophoneEndedListener();
    stopStream(this.microphoneStream);
    this.microphoneStream = null;
    this.captureManager.stop();
    stopStream(this._snapshot.remoteStream);
  }

  private detachMicrophoneEndedListener(): void {
    this.microphoneEndedCleanup?.();
    this.microphoneEndedCleanup = null;
  }

  private requireIdle(): void {
    if (this.disposed) throw new Error('Cannot start a disposed session manager.');
    if (this._snapshot.phase !== 'idle') {
      throw new Error(`Cannot start a session while ${this._snapshot.phase}.`);
    }
  }

  private isCurrentOperation(operationEpoch: number): boolean {
    return !this.disposed && this.operationEpoch === operationEpoch;
  }

  private requireCurrentOperation(operationEpoch: number): void {
    if (!this.isCurrentOperation(operationEpoch)) throw new SessionOperationCancelled();
  }

  private scheduleRoomExpiry(expiresAt: string | null): void {
    if (this.roomExpiryTimer !== null) clearTimeout(this.roomExpiryTimer);
    this.roomExpiryTimer = null;
    if (expiresAt === null) return;
    const delay = Math.max(0, Date.parse(expiresAt) - Date.now());
    this.roomExpiryTimer = setTimeout(
      () => {
        this.roomExpiryTimer = null;
        void this.end('The selected sharing time has ended.');
      },
      Math.min(delay, 2_147_483_647),
    );
  }

  private scheduleTurnRefresh(expiresAt: string | null): void {
    if (this.turnRefreshTimer !== null) clearTimeout(this.turnRefreshTimer);
    this.turnRefreshTimer = null;
    if (expiresAt === null || !this.managedTurnEnabled) return;

    const remaining = Date.parse(expiresAt) - Date.now();
    if (!Number.isFinite(remaining)) return;
    const refreshLead = Math.min(5 * 60_000, Math.max(60_000, remaining / 10));
    const delay = Math.max(1_000, remaining - refreshLead);
    this.turnRefreshTimer = setTimeout(
      () => {
        this.turnRefreshTimer = null;
        if (this._snapshot.phase !== 'active') return;
        try {
          this.signaling?.refreshTurnCredentials();
        } catch {
          this.scheduleTurnRefresh(new Date(Date.now() + 120_000).toISOString());
        }
      },
      Math.min(delay, 2_147_483_647),
    );
  }

  private update(patch: Partial<SessionSnapshot>): void {
    this._snapshot = Object.freeze({ ...this._snapshot, ...patch });
    this.publish();
  }

  private publish(): void {
    this.emit('change', { snapshot: this._snapshot });
  }
}

class SessionOperationCancelled extends Error {}

function viewerCredential(input: JoinRoomInput): RoomCredential | null {
  const inviteToken = input.inviteToken?.trim();
  if (inviteToken && /^[A-Za-z0-9_-]{32,128}$/u.test(inviteToken)) {
    return { type: 'invite-token', value: inviteToken };
  }
  const pin = input.pin?.trim() ?? '';
  return /^\d{4}$/u.test(pin) ? { type: 'pin', value: pin } : null;
}

function browserMicrophoneProvider() {
  return typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia
    ? null
    : { getUserMedia: navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices) };
}

function friendlyStartError(cause: unknown): string {
  if (cause instanceof CaptureError) {
    switch (cause.code) {
      case 'permission-denied':
      case 'aborted':
      case 'no-source':
        return 'Screen sharing was not started. Choose a screen, window, or tab to continue.';
      case 'insecure-context':
        return 'Screen sharing requires a secure HTTPS connection.';
      case 'unsupported':
        return 'This browser does not support screen sharing.';
      default:
        return 'The selected screen could not be shared. Please try again.';
    }
  }
  if (cause instanceof RoomApiError) return cause.message;
  const message = cause instanceof Error ? cause.message : '';
  if (message.includes('WRONG_PIN')) return 'That PIN is incorrect.';
  if (message.includes('ROOM_NOT_FOUND')) return 'That room no longer exists.';
  if (message.includes('ROOM_LOCKED')) return 'The host has locked this session.';
  if (message.includes('ROOM_FULL')) return 'This session already has a viewer.';
  if (message.includes('RATE_LIMITED'))
    return 'Too many PIN attempts. Wait a moment and try again.';
  if (message.includes('VIEWER_REMOVED')) return 'The host removed this viewer.';
  return 'The session could not connect. Check the details and try again.';
}

function peerLeftMessage(reason: PeerLeaveReason): string {
  switch (reason) {
    case 'host-ended':
      return 'The host ended this session.';
    case 'session-expired':
      return 'This session has expired.';
    case 'kicked':
      return 'The host disconnected you from this session.';
    case 'left':
      return 'The other participant left the session.';
  }
}

function leaveReason(role: PeerRole | null): PeerLeaveReason {
  return role === 'host' ? 'host-ended' : 'left';
}

function stopStream(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) {
    try {
      track.stop();
    } catch {
      // Best-effort cleanup of independently owned microphone tracks.
    }
  }
}
